import {map, publishReplay, distinctUntilChanged} from 'rxjs/operators';
/*
 * Source: https://github.com/AndrewPoyntz/time-ago-pipe
 * License: MIT
 *
 * Modified by Michael Fürnschuß - www.mf.at
 */

import {Pipe, PipeTransform, NgZone, ChangeDetectorRef, OnDestroy} from '@angular/core';
import {CacheService} from '../../../cache/cache.service';
import {ConnectableObservable, Subscription} from 'rxjs';
import {LanguageService} from '../../../language/language.service';
import moment from 'moment-timezone';
import 'moment/min/locales.js';

@Pipe({
  name: 'timeAgo',
  pure: false,
  standalone: false,
})
export class TimeAgoPipe implements PipeTransform, OnDestroy {
  private timer: number;

  private lrObs: ConnectableObservable<string> = null;
  private lrSubs: Subscription = null;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private ngZone: NgZone,
    cacheService: CacheService,
  ) {
    this.lrObs = cacheService
      .getLanguageResource()
      .pipe(distinctUntilChanged(), publishReplay(1)) as ConnectableObservable<any>;
    this.lrSubs = this.lrObs.connect();
  }

  transform(value: string) {
    this.removeTimer();
    let d = moment.utc(value).toDate();
    let now = new Date();
    let seconds = Math.round(Math.abs((now.getTime() - d.getTime()) / 1000));
    let timeToUpdate = this.getSecondsUntilUpdate(seconds) * 1000;
    this.timer = this.ngZone.runOutsideAngular(() => {
      if (typeof window !== 'undefined') {
        return window.setTimeout(() => {
          this.ngZone.run(() => this.changeDetectorRef.markForCheck());
        }, timeToUpdate);
      }
      return null;
    });
    let minutes = Math.round(Math.abs(seconds / 60));
    let hours = Math.round(Math.abs(minutes / 60));
    let days = Math.round(Math.abs(hours / 24));
    let months = Math.round(Math.abs(days / 30.416));
    let years = Math.round(Math.abs(days / 365));
    return this.lrObs.pipe(
      map(lr => {
        if (seconds <= 45) {
          return LanguageService.translate('TimeAgoPipeFewSeconds', 'a few seconds ago', lr);
        } else if (seconds <= 90) {
          return LanguageService.translate('TimeAgoPipeAMinute', 'a minute ago', lr);
        } else if (minutes <= 45) {
          return LanguageService.translate('TimeAgoPipeSomeMinutes', '{0} minutes ago', lr, [
            String(minutes),
          ]);
        } else if (minutes <= 90) {
          return LanguageService.translate('TimeAgoPipeAnHour', 'an hour ago', lr);
        } else if (hours <= 22) {
          return LanguageService.translate('TimeAgoPipeSomeHours', '{0} hours ago', lr, [
            String(hours),
          ]);
        } else if (hours <= 36) {
          return LanguageService.translate('TimeAgoPipeADay', 'a day ago', lr);
        } else if (days <= 25) {
          return LanguageService.translate('TimeAgoPipeSomeDays', '{0} days ago', lr, [
            String(days),
          ]);
        } else if (days <= 45) {
          return LanguageService.translate('TimeAgoPipeAMonth', 'a month ago', lr);
        } else if (days <= 345) {
          return LanguageService.translate('TimeAgoPipeSomeMonths', '{0} months ago', lr, [
            String(months),
          ]);
        } else if (days <= 545) {
          return LanguageService.translate('TimeAgoPipeAYear', 'a year ago', lr);
        } else {
          // (days > 545)
          return LanguageService.translate('TimeAgoPipeSomeYears', '{0} years ago', lr, [
            String(years),
          ]);
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.removeTimer();
    this.lrSubs.unsubscribe();
  }

  private removeTimer() {
    if (this.timer) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private getSecondsUntilUpdate(seconds: number) {
    let min = 60;
    let hr = min * 60;
    let day = hr * 24;
    if (seconds < min) {
      // less than 1 min, update ever 2 secs
      return 2;
    } else if (seconds < hr) {
      // less than an hour, update every 30 secs
      return 30;
    } else if (seconds < day) {
      // less then a day, update every 5 mins
      return 300;
    } else {
      // update every hour
      return 3600;
    }
  }
}
